import { createSlice, AnyAction, isAnyOf } from "@reduxjs/toolkit";
import {
  createSlot,
  updateSlot,
  deleteSlot,
  extendSlot,
  refundSlot,
  batchExtend,
  batchRefund,
  fetchSlots,
  fetchSlotsPaginationInfo,
  batchCreateSlots,
  batchUpdateSlots,
} from "./actions";
import { Slot, PaginationInfo, AppliedFilters } from "../Model/Slot";
import { calcRemaining } from "../Domain/UseCase/Utils/DateUtil";
import { shouldKeepRankPending } from "../../../Utils/slotRankPolling";

function prependSlotsToState(state: SlotState, slots: Slot[]) {
  if (slots.length === 0) return;
  const ps = state.pagination?.page_size ?? 20;
  state.list = [...slots, ...state.list].slice(0, ps);
  if (state.pagination?.total_count != null) {
    state.pagination.total_count += slots.length;
  }
  if (state.defaultPage.loaded) {
    const dps = state.defaultPage.pagination?.page_size ?? 20;
    state.defaultPage.slots = [...slots, ...state.defaultPage.slots].slice(0, dps);
    if (state.defaultPage.pagination?.total_count != null) {
      state.defaultPage.pagination.total_count += slots.length;
    }
  }
}

function removeSlotsFromState(state: SlotState, ids: number[]) {
  const idSet = new Set(ids);
  const beforeList = state.list.length;
  state.list = state.list.filter((v) => !idSet.has(v.slotId));
  const removedList = beforeList - state.list.length;
  if (removedList > 0 && state.pagination?.total_count != null) {
    state.pagination.total_count = Math.max(0, state.pagination.total_count - removedList);
  }
  if (state.defaultPage.loaded) {
    const beforeDefault = state.defaultPage.slots.length;
    state.defaultPage.slots = state.defaultPage.slots.filter((v) => !idSet.has(v.slotId));
    const removedDefault = beforeDefault - state.defaultPage.slots.length;
    if (removedDefault > 0 && state.defaultPage.pagination?.total_count != null) {
      state.defaultPage.pagination.total_count = Math.max(
        0,
        state.defaultPage.pagination.total_count - removedDefault,
      );
    }
  }
}

function replaceOptimisticSlot(state: SlotState, tempId: number, saved: Slot) {
  const slot: Slot = {
    ...saved,
    remaining_days: calcRemaining(saved.end_date),
    // 생성 직후 today_rank가 null인 것은 정상(비동기). 키워드+URL 있으면 폴링까지 pending 유지
    _rankPending: shouldKeepRankPending(saved),
    _optimistic: false,
  };
  const idx = state.list.findIndex((v) => v.slotId === tempId);
  if (idx >= 0) state.list[idx] = slot;
  if (state.defaultPage.loaded) {
    const dIdx = state.defaultPage.slots.findIndex((v) => v.slotId === tempId);
    if (dIdx >= 0) state.defaultPage.slots[dIdx] = slot;
  }
}

function patchSlotInState(state: SlotState, slotId: number, patch: Partial<Slot>) {
  const idx = state.list.findIndex((v) => v.slotId === slotId);
  if (idx >= 0) state.list[idx] = { ...state.list[idx], ...patch };
  if (state.defaultPage.loaded) {
    const dIdx = state.defaultPage.slots.findIndex((v) => v.slotId === slotId);
    if (dIdx >= 0) {
      state.defaultPage.slots[dIdx] = { ...state.defaultPage.slots[dIdx], ...patch };
    }
  }
}

function applyBatchExtendToSlot(target: Slot, days: number) {
  target.status = "연장";
  target.remaining_days = Math.max(0, (target.remaining_days || 0) + days);
  const currentDate = new Date();
  const endDate = new Date(currentDate);
  endDate.setDate(currentDate.getDate() + target.remaining_days);
  target.end_date = endDate.toISOString().split("T")[0];
}

function applyBatchRefundToSlot(target: Slot) {
  target.status = "환불";
  target.remaining_days = 0;
  const currentDate = new Date();
  target.end_date = currentDate.toISOString().split("T")[0];
}

interface SlotState {
  list: Slot[];
  pagination: PaginationInfo | null;
  filters: AppliedFilters | null;
  loading: boolean;
  error?: string;
  /** 목록 조회 fetchSlots 요청 중 가장 최근 requestId (오래된 응답 적용 방지) */
  activeListFetchRequestId?: string;
  defaultPage: {
    slots: Slot[];
    pagination: PaginationInfo | null;
    loaded: boolean;
  };
  // 데이터 새로고침이 필요한지 표시하는 플래그
  needsRefresh: boolean;
}

const initialState: SlotState = { 
  list: [], 
  pagination: null,
  filters: null,
  loading: false,
  activeListFetchRequestId: undefined,
  defaultPage: {
    slots: [],
    pagination: null,
    loaded: false
  },
  needsRefresh: false
};

/* ──────────────────────────────── 공통 Matcher ─────────────────────────────── */

const isSlotPending = isAnyOf(
  // 테이블 오버레이는 목록을 가져올 때만 사용한다. 개별 저장은 각 행의
  // 낙관적 상태로 처리하므로 다른 행의 조작을 막으면 안 된다.
  fetchSlotsPaginationInfo.pending,
);

const isSlotRejected = isAnyOf(
  fetchSlotsPaginationInfo.rejected,
);

// 데이터 변경 액션 중 목록을 직접 반영하지 않는 것만 새로고침 플래그 (엑셀 일괄 수정 등)
const isDataModifyingAction = isAnyOf(batchUpdateSlots.fulfilled);

/* ───────────────────────────────────── Slice ───────────────────────────────── */

const slotSlice = createSlice({
  name: "slot",
  initialState,
  reducers: {
    optimisticUpdateSlot: (state, action) => {
      const { id, dto } = action.payload as { id: number; dto: Partial<Slot> };
      const idx = state.list.findIndex((v) => v.slotId === id);
      if (idx >= 0) state.list[idx] = { ...state.list[idx], ...dto };
      if (state.defaultPage.loaded) {
        const dIdx = state.defaultPage.slots.findIndex((v) => v.slotId === id);
        if (dIdx >= 0) {
          state.defaultPage.slots[dIdx] = { ...state.defaultPage.slots[dIdx], ...dto };
        }
      }
    },
    markSlotsRankPending: (state, action) => {
      const { ids, invalidateRank } = action.payload as {
        ids: number[];
        invalidateRank?: boolean;
      };
      ids.forEach((id) => {
        patchSlotInState(state, id, {
          _rankPending: true,
          rank_check_status: 'pending',
          ...(invalidateRank ? { today_rank: null } : {}),
        });
      });
    },
    applyRankPollResults: (state, action) => {
      const { slots, clearPendingIds } = action.payload as {
        slots?: Slot[];
        clearPendingIds?: number[];
      };
      (slots ?? []).forEach((fetched) => {
        const patch: Partial<Slot> = {
          today_rank: fetched.today_rank,
          yesterday_rank: fetched.yesterday_rank,
          rank_history: fetched.rank_history,
          rank_check_status: fetched.rank_check_status,
          mid_single: fetched.mid_single,
          mid_compare: fetched.mid_compare,
          target_mid: fetched.target_mid,
          mb_id: fetched.mb_id,
          _rankPending: shouldKeepRankPending(fetched),
        };
        patchSlotInState(state, fetched.slotId, patch);
      });
      (clearPendingIds ?? []).forEach((id) => {
        patchSlotInState(state, id, { _rankPending: false });
      });
    },
    optimisticBatchExtend: (state, action) => {
      const { ids, days } = action.payload as { ids: number[]; days: number };
      ids.forEach((id) => {
        const target = state.list.find((s) => s.slotId === id);
        if (target) applyBatchExtendToSlot(target, days);
        if (state.defaultPage.loaded) {
          const d = state.defaultPage.slots.find((s) => s.slotId === id);
          if (d) applyBatchExtendToSlot(d, days);
        }
      });
    },
    optimisticBatchRefund: (state, action) => {
      const { ids } = action.payload as { ids: number[] };
      ids.forEach((id) => {
        const target = state.list.find((s) => s.slotId === id);
        if (target) applyBatchRefundToSlot(target);
        if (state.defaultPage.loaded) {
          const d = state.defaultPage.slots.find((s) => s.slotId === id);
          if (d) applyBatchRefundToSlot(d);
        }
      });
    },
    optimisticAddSlots: (state, action) => {
      prependSlotsToState(state, action.payload.slots as Slot[]);
    },
    optimisticDeleteSlots: (state, action) => {
      removeSlotsFromState(state, action.payload.ids as number[]);
    },
    removeOptimisticSlots: (state, action) => {
      removeSlotsFromState(state, action.payload.tempIds as number[]);
    },
    rollbackSlots: (state, action) => {
      const snapshots = action.payload as Array<{
        id: number;
        listSlot?: Slot;
        defaultSlot?: Slot;
      }>;
      snapshots.forEach(({ id, listSlot, defaultSlot }) => {
        const idx = state.list.findIndex((v) => v.slotId === id);
        if (idx >= 0 && listSlot) {
          state.list[idx] = listSlot;
        } else if (listSlot && !state.list.some((v) => v.slotId === id)) {
          state.list.unshift(listSlot);
          if (state.pagination?.total_count != null) state.pagination.total_count += 1;
        }
        if (state.defaultPage.loaded) {
          const dIdx = state.defaultPage.slots.findIndex((v) => v.slotId === id);
          if (dIdx >= 0 && defaultSlot) {
            state.defaultPage.slots[dIdx] = defaultSlot;
          } else if (defaultSlot && !state.defaultPage.slots.some((v) => v.slotId === id)) {
            state.defaultPage.slots.unshift(defaultSlot);
            if (state.defaultPage.pagination?.total_count != null) {
              state.defaultPage.pagination.total_count += 1;
            }
          }
        }
      });
    },
    setDefaultPage: (state, action) => {
      state.defaultPage = {
        slots: action.payload.slots,
        pagination: action.payload.pagination,
        loaded: true
      };
    },
    clearDefaultPage: (state) => {
      state.defaultPage = {
        slots: [],
        pagination: null,
        loaded: false
      };
    },
    // 데이터 새로고침 플래그 리셋
    resetRefreshFlag: (state) => {
      state.needsRefresh = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSlots.pending, (state, action) => {
        state.activeListFetchRequestId = action.meta.requestId;
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchSlots.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.activeListFetchRequestId) {
          return;
        }
        const pendingIds = new Set<number>();
        state.list.forEach((slot) => {
          if (slot._rankPending) pendingIds.add(slot.slotId);
        });
        if (state.defaultPage.loaded) {
          state.defaultPage.slots.forEach((slot) => {
            if (slot._rankPending) pendingIds.add(slot.slotId);
          });
        }
        state.list = action.payload.slots.map((slot) =>
          pendingIds.has(slot.slotId) ? { ...slot, _rankPending: true } : slot,
        );
        state.pagination = action.payload.pagination;
        state.filters = action.payload.filters;
        state.loading = false;
        state.needsRefresh = false; // 새로고침 완료
      })
      .addCase(fetchSlots.rejected, (state, action) => {
        const err = action.error as { name?: string; code?: string } | undefined;
        const canceled =
          action.meta.aborted ||
          err?.name === 'CanceledError' ||
          err?.code === 'ERR_CANCELED';
        if (canceled) {
          if (action.meta.requestId === state.activeListFetchRequestId) {
            state.loading = false;
          }
          return;
        }
        if (action.meta.requestId !== state.activeListFetchRequestId) {
          return;
        }
        state.loading = false;
        state.error = action.error?.message;
      })
      .addCase(fetchSlotsPaginationInfo.fulfilled, (state, action) => {
        // 페이지네이션 정보만 업데이트 (슬롯 목록은 변경하지 않음)
        state.loading = false;
      })
      .addCase(createSlot.fulfilled, (state, action) => {
        const { optimisticApplied, tempId } = action.meta.arg;
        if (optimisticApplied && tempId != null) {
          replaceOptimisticSlot(state, tempId, action.payload);
          state.loading = false;
          state.needsRefresh = false;
          return;
        }
        prependSlotsToState(state, [action.payload]);
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(updateSlot.fulfilled, (state, action) => {
        const saved = action.payload;
        const next: Slot = {
          ...saved,
          remaining_days: calcRemaining(saved.end_date),
          _rankPending: shouldKeepRankPending(saved),
          _optimistic: false,
        };
        const idx = state.list.findIndex(
          (v) => v.slotId === next.slotId
        );
        if (idx >= 0) state.list[idx] = next;
        if (state.defaultPage.loaded) {
          const dIdx = state.defaultPage.slots.findIndex(
            (v) => v.slotId === next.slotId
          );
          if (dIdx >= 0) state.defaultPage.slots[dIdx] = next;
        }
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(deleteSlot.fulfilled, (state, action) => {
        if (action.meta.arg.optimisticApplied) {
          state.loading = false;
          state.needsRefresh = false;
          return;
        }
        const id = action.meta.arg.id;
        removeSlotsFromState(state, [id]);
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(extendSlot.fulfilled, (state, action) => {
        const idx = state.list.findIndex(
          (v) => v.slotId === action.payload.slotId
        );
        if (idx >= 0) state.list[idx] = action.payload;
        if (state.defaultPage.loaded) {
          const dIdx = state.defaultPage.slots.findIndex(
            (v) => v.slotId === action.payload.slotId
          );
          if (dIdx >= 0) state.defaultPage.slots[dIdx] = action.payload;
        }
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(refundSlot.fulfilled, (state, action) => {
        const idx = state.list.findIndex(
          (v) => v.slotId === action.payload.slotId
        );
        if (idx >= 0) state.list[idx] = action.payload;
        if (state.defaultPage.loaded) {
          const dIdx = state.defaultPage.slots.findIndex(
            (v) => v.slotId === action.payload.slotId
          );
          if (dIdx >= 0) state.defaultPage.slots[dIdx] = action.payload;
        }
        state.loading = false;
        state.needsRefresh = false;
      })
      /* ---------- batch fulfilled ---------- */
      .addCase(batchExtend.fulfilled, (state, action) => {
        if (action.meta.arg.optimisticApplied) {
          state.loading = false;
          state.needsRefresh = false;
          return;
        }
        const { days } = action.meta.arg;
        action.payload.forEach((id) => {
          const target = state.list.find((s) => s.slotId === id);
          if (target) applyBatchExtendToSlot(target, days);
          if (state.defaultPage.loaded) {
            const d = state.defaultPage.slots.find((s) => s.slotId === id);
            if (d) applyBatchExtendToSlot(d, days);
          }
        });
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(batchRefund.fulfilled, (state, action) => {
        if (action.meta.arg.optimisticApplied) {
          state.loading = false;
          state.needsRefresh = false;
          return;
        }
        action.payload.forEach((id) => {
          const target = state.list.find((s) => s.slotId === id);
          if (target) applyBatchRefundToSlot(target);
          if (state.defaultPage.loaded) {
            const d = state.defaultPage.slots.find((s) => s.slotId === id);
            if (d) applyBatchRefundToSlot(d);
          }
        });
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(batchCreateSlots.fulfilled, (state, action) => {
        const { optimisticApplied, tempIds } = action.meta.arg;
        if (optimisticApplied && tempIds?.length && Array.isArray(action.payload)) {
          action.payload.forEach((saved, index) => {
            const tempId = tempIds[index];
            if (tempId != null) replaceOptimisticSlot(state, tempId, saved);
          });
          state.loading = false;
          state.needsRefresh = false;
          return;
        }
        if (Array.isArray(action.payload) && action.payload.length > 0) {
          prependSlotsToState(state, action.payload);
        }
        state.loading = false;
        state.needsRefresh = false;
      })

      /* ---------- 공통 pending/rejected ---------- */
      .addMatcher(isSlotPending, (state, action) => {
        const arg = action.meta?.arg as { optimisticApplied?: boolean } | undefined;
        if (arg?.optimisticApplied) return;
        state.loading = true;
        state.error = undefined;
      })
      .addMatcher(isSlotRejected, (state, action: AnyAction) => {
        state.loading = false;
        state.error = action.error?.message;
      })
      /* ---------- 데이터 변경 액션들 ---------- */
      .addMatcher(isDataModifyingAction, (state) => {
        // 데이터 변경 액션 완료 시 새로고침 플래그 설정
        state.needsRefresh = true;
      });
  },
});

export const {
  optimisticUpdateSlot,
  markSlotsRankPending,
  applyRankPollResults,
  optimisticBatchExtend,
  optimisticBatchRefund,
  optimisticAddSlots,
  optimisticDeleteSlots,
  removeOptimisticSlots,
  rollbackSlots,
  setDefaultPage,
  clearDefaultPage,
  resetRefreshFlag,
} = slotSlice.actions;
export default slotSlice.reducer;
